import React, { useState, useEffect } from 'react';
import { userApi } from '../services/userApi';
import Navigation from '../components/Navigation';
import ConfirmModal from '../components/ConfirmModal';
import '../styles/UserManagement.css';

function UserManagement({ onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('Todas as Roles');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'User'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await userApi.listUsers();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Erro ao carregar usuários. Você precisa ser Admin para acessar esta página.');
    } finally {
      setLoading(false);
    }
  };

  const getAvatarColor = (name) => {
    const colors = ['#5a67d8', '#ed8936', '#48bb78', '#ed64a6', '#4299e1'];
    const charCode = name.charCodeAt(0);
    return colors[charCode % colors.length];
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'Todas as Roles' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      await userApi.createUser(formData);
      setShowModal(false);
      setFormData({ email: '', password: '', role: 'User' });
      loadUsers();
    } catch (err) {
      console.error('Erro ao criar usuário:', err);
      setFormError(err.response?.data?.error || 'Erro ao criar usuário');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = (username) => {
    setConfirmDialog({
      message: `Tem certeza que deseja deletar o usuário ${username}?`,
      onConfirm: async () => {
        try {
          await userApi.deleteUser(username);
          loadUsers();
        } catch (err) {
          console.error('Erro ao deletar usuário:', err);
          alert('Erro ao deletar usuário');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleRoleChange = (username, currentRole) => {
    const newRole = currentRole === 'Admin' ? 'User' : 'Admin';

    setConfirmDialog({
      message: `Alterar role de ${username} para ${newRole}?`,
      onConfirm: async () => {
        try {
          await userApi.updateUserRole(username, newRole);
          loadUsers();
        } catch (err) {
          console.error('Erro ao atualizar role:', err);
          alert('Erro ao atualizar role');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  if (loading) {
    return (
      <div className="user-management-page">
        <Navigation onLogout={onLogout} />
        <div className="container">
          <div className="loading">Carregando usuários...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management-page">
        <Navigation onLogout={onLogout} />
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <Navigation onLogout={onLogout} />

      <div className="container">

        <div className="page-header">
          <div>
            <h1>Gerenciamento de Usuários</h1>
            <p>Crie e gerencie os níveis de acesso dos usuários.</p>
          </div>
          <button className="btn-new" onClick={() => setShowModal(true)}>
            + Novo Usuário
          </button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Buscar por e-mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option>Todas as Roles</option>
            <option>Admin</option>
            <option>User</option>
          </select>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Criado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.username}>
                  <td>
                    <div className="user-info">
                      <div
                        className="avatar"
                        style={{ background: getAvatarColor(user.email) }}
                      >
                        {user.email.charAt(0).toUpperCase()}
                      </div>
                      {user.email}
                    </div>
                  </td>
                  <td>
                    <span className={`badge badge-${user.role.toLowerCase()}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${user.status.toLowerCase()}`}>
                      {user.status}
                    </span>
                  </td>
                  <td style={{ color: '#718096' }}>
                    {new Date(user.created).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn-action"
                      onClick={() => handleRoleChange(user.username, user.role)}
                      title="Trocar nível de acesso"
                    >
                      Trocar Role
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() => handleDeleteUser(user.username)}
                    >
                      Deletar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="empty-state">
              <p>Nenhum usuário encontrado.</p>
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Criar Novo Usuário</h2>
                <button className="close-button" onClick={() => setShowModal(false)}>
                  ×
                </button>
              </div>

              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="usuario@exemplo.com"
                  />
                </div>

                <div className="form-group">
                  <label>Senha:</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="Mínimo 8 caracteres"
                    minLength="8"
                  />
                  <small>Use letras maiúsculas, minúsculas e números.</small>
                </div>

                <div className="form-group">
                  <label>Role:</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="User">User</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>

                {formError && <div className="error-message" style={{ color: '#c53030', background: '#fff5f5', border: '1px solid #feb2b2' }}>{formError}</div>}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={formLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Criando...' : 'Criar Usuário'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {confirmDialog && (
          <ConfirmModal
            message={confirmDialog.message}
            onConfirm={confirmDialog.onConfirm}
            onCancel={confirmDialog.onCancel}
          />
        )}
      </div>
    </div>
  );
}

export default UserManagement;